import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/Select';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from 'hooks/useAuth';
import { Slack } from 'lucide-react';
import { useSlackSettings } from '@/state/notifications/useSlackSettings';
import { NotificationMatrix } from './NotificationMatrix';

const ADMIN_ROLES = ['Owner', 'Admin', 'Super Admin'];

/**
 * The organisation's Slack connection and routing.
 *
 * Company-scoped, and on the organisation screen for that reason: one channel
 * covers every website in the account, so this is configuration a Member should
 * not be changing. Personal email preferences are on the account screen.
 */
export const SlackNotifications = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const isAdmin = ADMIN_ROLES.includes(user?.role ?? '');

  const manager = useSlackSettings(isAdmin);
  const { slack, settings, isConnected, isLoading, channels, isLoadingChannels, isSaving } = manager;

  const [isInstalling, setIsInstalling] = useState(false);

  /**
   * Slack sends the admin back here with ?code&state after the consent screen.
   * The code is single-use, so it is stripped from the URL as soon as it is read -
   * otherwise a refresh replays a code Slack has already spent and shows an error
   * for an install that actually succeeded.
   */
  useEffect(() => {
    if (!isAdmin) return;

    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    if (!code || !state) return;

    params.delete('code');
    params.delete('state');
    const query = params.toString();
    window.history.replaceState({}, '', `${window.location.pathname}${query ? `?${query}` : ''}`);

    manager
      .connect({ code, state })
      .then(() => toast({ title: 'Slack connected', description: 'Now pick the channel to post into.' }))
      .catch(() =>
        toast({
          title: 'Could not connect Slack',
          description: 'The installation link may have expired. Please try again.',
          variant: 'destructive',
        }),
      );
  }, [isAdmin]);

  // Members do not get a read-only view of the workspace the company posts into.
  if (!isAdmin) return null;

  const onInstall = async () => {
    setIsInstalling(true);
    try {
      await manager.startInstall();
    } catch {
      setIsInstalling(false);
      toast({
        title: 'Slack is unavailable',
        description: 'Slack is not configured on this environment.',
        variant: 'destructive',
      });
    }
  };

  const onDisconnect = () => {
    manager.disconnect();
    toast({ title: 'Slack disconnected', description: 'Nothing further will be posted to your workspace.' });
  };

  const channelOptions = channels.map((channel) => ({
    value: channel.id,
    label: `${channel.isPrivate ? '🔒' : '#'}${channel.name}`,
  }));

  const onChannelChange = (channelId: string) => {
    const channel = channels.find((candidate) => candidate.id === channelId);
    if (channel) manager.selectChannel(channel);
  };

  return (
    <section className="bg-panel border-line rounded-panel border" data-tour="settings-general-slack">
      <div className="border-line border-b px-4 py-3">
        <div className="flex items-center gap-2">
          <Slack className="text-dim h-4 w-4" />
          <h2 className="text-foreground m-0 text-body font-medium">Slack</h2>
          {isConnected && (
            <span className="micro-label rounded-pill border-ok-line text-ok ml-auto border px-2 py-0.5">
              connected
            </span>
          )}
        </div>
        <p className="supporting m-0 mt-1">
          Post notifications into one channel, covering every website in this organisation.
        </p>
      </div>

      <div className="space-y-5 p-4">
        {isLoading ? (
          <div className="bg-field rounded-card h-10 animate-pulse" />
        ) : !isConnected ? (
          <div className="space-y-3">
            <p className="supporting m-0">
              Connect a Slack workspace to start posting. You choose the channel in the next step.
            </p>
            <Button onClick={onInstall} disabled={isInstalling} className="h-9 rounded-lg">
              {isInstalling ? 'Opening Slack...' : 'Add to Slack'}
            </Button>
          </div>
        ) : (
          <>
            <div className="bg-panel2 border-line rounded-card flex items-center justify-between gap-4 border p-3">
              <div className="min-w-0">
                <p className="text-foreground m-0 truncate text-meta font-medium">
                  {slack?.teamName || 'Slack workspace'}
                </p>
                <p className="micro-label mt-1">
                  {slack?.channelName ? `Posting to #${slack.channelName}` : 'No channel chosen yet'}
                </p>
              </div>
              <Button
                variant="outline"
                onClick={onDisconnect}
                className="border-line text-foreground hover:bg-hover h-9 shrink-0 rounded-lg bg-transparent"
              >
                Disconnect
              </Button>
            </div>

            <div className="space-y-2">
              <Select
                options={channelOptions}
                value={slack?.channelId ?? null}
                onValueChange={onChannelChange}
                placeholder={isLoadingChannels ? 'Loading channels...' : 'Select a channel'}
                className="border-line bg-field text-foreground h-10 rounded-lg text-body"
              />
              <p className="supporting m-0">Private channels need the Pertento app invited before posts will arrive.</p>
            </div>

            {/* A plain block for the product tour: NotificationMatrix is shared with the account screen. */}
            <div data-tour="settings-general-slack-events">
              <NotificationMatrix
                settings={settings}
                onToggle={manager.toggle}
                disabled={isSaving || !slack?.channelId}
                lockedReason={!slack?.channelId ? 'Pick a channel to choose what gets posted.' : undefined}
              />
            </div>
          </>
        )}
      </div>
    </section>
  );
};
